import React from 'react';
import type { DeepReadonly } from '@itandi/itandi-bb-ui';
import {
  Button,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Modalv2,
  FormControl,
  FormControlText,
  Input,
  Box
} from '@itandi/itandi-bb-ui';
import { useForm, FormProvider } from 'react-hook-form';
import { toast } from 'react-toastify';
import type {
  LineUserAccount,
  LineUserAccountEdit
} from '~/models/LineUserAccount';
import { buildInitialLineUserAccount } from '~/models/LineUserAccount';
import { isSuccess } from '~/utils/api/api_handler';
import type { NskResponse } from '~/models/Api';

type Props = DeepReadonly<{
  isOpen: boolean;
  handleClose: () => void;
  handleMutate: () => Promise<void>;
  lineUserAccount?: LineUserAccount | null;
  handleCreate?:
    | ((lineUserAccount: LineUserAccountEdit) => Promise<NskResponse>)
    | null;
  handleUpdate?:
    | ((
        lineUserAccount: LineUserAccountEdit,
        lineUserAccountId: number
      ) => Promise<NskResponse>)
    | null;
}>;

export const LineUserAccountModal: React.FC<Props> = ({
  isOpen,
  handleClose,
  handleCreate = null,
  handleUpdate = null,
  handleMutate,
  lineUserAccount = null
}: Props) => {
  const methods = useForm<LineUserAccountEdit>({
    defaultValues: lineUserAccount ?? buildInitialLineUserAccount(),
    mode: 'onBlur'
  });
  const { errors } = methods.formState;
  const newRecord = lineUserAccount === null;
  const handleSubmit = React.useCallback(() => {
    methods.handleSubmit(async () => {
      let result;
      if (newRecord) {
        if (handleCreate === null) {
          return;
        }
        result = await handleCreate(methods.getValues());
      } else {
        if (handleUpdate === null || lineUserAccount === null) {
          return;
        }
        result = await handleUpdate(methods.getValues(), lineUserAccount.id);
      }
      await handleMutate();
      if (isSuccess(result)) {
        toast.success('保存しました');
        handleClose();
        methods.reset();
        return;
      }
      toast.error(`エラーが発生しました: ${result.message}`);
    })();
  }, [
    handleClose,
    handleCreate,
    handleMutate,
    handleUpdate,
    lineUserAccount,
    methods,
    newRecord
  ]);
  return (
    <Modalv2 isOpen={isOpen} onClose={handleClose} size="l">
      <ModalHeader title="LINEユーザーアカウント設定" />
      <ModalBody>
        <FormProvider {...methods}>
          {newRecord && (
            <Box marginBottom={2}>
              <FormControl
                isInvalid={errors.token != null}
                label="認証コード"
                variant="Horizontal"
              >
                <Input
                  {...methods.register('token', {
                    required: '入力してください'
                  })}
                  isInvalid={errors.token != null}
                  placeholder="認証コード"
                />
                {errors.token != null && (
                  <FormControlText error={errors.token.message} />
                )}
              </FormControl>
            </Box>
          )}
          <Box marginBottom={2}>
            <FormControl
              isInvalid={errors.name != null}
              label="名前"
              variant="Horizontal"
            >
              <Input
                {...methods.register('name', { required: '入力してください' })}
                isInvalid={errors.name != null}
                placeholder="送信先の名前"
              />
              {errors.name != null && (
                <FormControlText error={errors.name.message} />
              )}
            </FormControl>
          </Box>
        </FormProvider>
      </ModalBody>
      <ModalFooter
        leftArea={
          <Button onClick={handleClose} variant="secondary">
            保存せず閉じる
          </Button>
        }
        rightArea={
          <Button onClick={handleSubmit} variant="primary">
            {newRecord ? '追加' : '更新'}
          </Button>
        }
      />
    </Modalv2>
  );
};
